import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Advertisement {
  _id: string;
  adminId: string;
  mediaType: string;
  mediaUrl: string;
  title: string;
  description: string;
  adType: string;
  isDeleted: boolean;
  startDate: string;
  endDate: string;
  status: string;
  subscriptionType: string;
  viewedBy: string[];
  clickedBy: string[];
  createdAt: string;
  updatedAt: string;
  embedLink: string;
  impression: {
    count: number;
  }[];
  actionLabel: string;
}

export interface AdAnalyticsPoint {
  period: string;
  week: number;
  views: number;
  clicks: number;
  impressions: number;
  uniqueUsers: number;
}

export interface AdAnalytics {
  adId: string;
  title: string;
  dataPoints: AdAnalyticsPoint[];
}

export interface Impression {
  _id: string;
  count: number;
}

export interface SingleAdAnalyticsPoint {
  _id: string;
  adminId: string;
  mediaType: "image" | "video";
  mediaUrl: string;
  title: string;
  description: string;
  adType: string;
  isDeleted: boolean;
  startDate: string;
  endDate: string;
  status: string;
  subscriptionType: string;
  viewedBy: string[];
  clickedBy: string[];
  createdAt: string;
  updatedAt: string;
  impression: Impression[];
}


export interface SingleAdAnalytics {
  ad: SingleAdAnalyticsPoint;
}

export interface UploadFormData {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  embedLink?: string;
  subscriptionType?: string;
  actionLabel?: string;
  file?: File | null;
  adType: string;
}

export interface AdvertisementUpdatePayload {
  ad_id: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  embedLink?: string;
  subscriptionType?: string;
  actionLabel?: string;
  file?: File | null;
  adType?: string;
}

export interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdType {
  label: string;
  slug: string;
  color: string;
  size: string;
}

export interface AdsState {
  // Data
  ads: Advertisement[];
  selectedAd: Advertisement | null;
  analytics: AdAnalytics | null;
  analyticsInfo: SingleAdAnalytics | null;
  adTypes: AdType[];

  // Pagination
  pagination: PaginationData;
  currentPage: number;

  // Loading states
  loading: boolean;
  loadingSingle: boolean;
  exporting: boolean;
  uploading: boolean;
  updating: boolean;
  deleting: boolean;

  // Filters
  selectedAdType: string;
  searchQuery: string;
  statusFilter: string;

  // UI state
  showUploadModal: boolean;
  showEditModal: boolean;
  showDeleteModal: boolean;

  // Error handling
  error: string | null;
  uploadError: string | null;
  updateError: string | null;
  deleteError: string | null;
}

const initialAdTypes: AdType[] = [
  { label: "Full-screen", slug: "full", color: "#7607BD", size: "340 x 800" },
  { label: "Slider", slug: "carousel", color: "#55C4F1", size: "340 x 189" },
  { label: "Pop-ups", slug: "banner", color: "#FFAD4A", size: "340 x 340" },
];

const initialState: AdsState = {
  // Data
  ads: [],
  selectedAd: null,
  adTypes: initialAdTypes,
  analytics: null,
  analyticsInfo: null,

  // Pagination
  pagination: {
    page: 0,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  currentPage: 1,

  // Loading states
  loading: false,
  loadingSingle: false,
  exporting: false,
  uploading: false,
  updating: false,
  deleting: false,

  // Filters
  selectedAdType: "",
  searchQuery: "",
  statusFilter: "",

  // UI state
  showUploadModal: false,
  showEditModal: false,
  showDeleteModal: false,

  // Error handling
  error: null,
  uploadError: null,
  updateError: null,
  deleteError: null,
};

const adsSlice = createSlice({
  name: "ads",
  initialState,
  reducers: {
    // Data actions
    setAds: (state, action: PayloadAction<Advertisement[]>) => {
      state.ads = action.payload;
    },
    addAd: (state, action: PayloadAction<Advertisement>) => {
      state.ads.unshift(action.payload);
    },
    updateAd: (state, action: PayloadAction<Advertisement>) => {
      const index = state.ads.findIndex((ad) => ad._id === action.payload._id);
      if (index !== -1) {
        state.ads[index] = action.payload;
      }
    },
    removeAd: (state, action: PayloadAction<string>) => {
      state.ads = state.ads.filter((ad) => ad._id !== action.payload);
    },
    setSelectedAd: (state, action: PayloadAction<Advertisement | null>) => {
      state.selectedAd = action.payload;
    },

    setAdAnalytics(state, action: PayloadAction<AdAnalytics>) {
      state.analytics = action.payload;
    },

    setSingleAdAnalytics(state, action: PayloadAction<SingleAdAnalytics>) {
      state.analyticsInfo = action.payload;
    },

    // Pagination actions
    setPagination: (state, action: PayloadAction<PaginationData>) => {
      state.pagination = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    // Loading actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingSingle: (state, action: PayloadAction<boolean>) => {
      state.loadingSingle = action.payload;
    },
    setExporting: (state, action: PayloadAction<boolean>) => {
      state.exporting = action.payload;
    },
    setUploading: (state, action: PayloadAction<boolean>) => {
      state.uploading = action.payload;
    },
    setUpdating: (state, action: PayloadAction<boolean>) => {
      state.updating = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.deleting = action.payload;
    },

    // Filter actions
    setSelectedAdType: (state, action: PayloadAction<string>) => {
      state.selectedAdType = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.statusFilter = action.payload;
    },

    // UI actions
    setShowUploadModal: (state, action: PayloadAction<boolean>) => {
      state.showUploadModal = action.payload;
    },
    setShowEditModal: (state, action: PayloadAction<boolean>) => {
      state.showEditModal = action.payload;
    },
    setShowDeleteModal: (state, action: PayloadAction<boolean>) => {
      state.showDeleteModal = action.payload;
    },

    // Error actions
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setUploadError: (state, action: PayloadAction<string | null>) => {
      state.uploadError = action.payload;
    },
    setUpdateError: (state, action: PayloadAction<string | null>) => {
      state.updateError = action.payload;
    },
    setDeleteError: (state, action: PayloadAction<string | null>) => {
      state.deleteError = action.payload;
    },
    clearErrors: (state) => {
      state.error = null;
      state.uploadError = null;
      state.updateError = null;
      state.deleteError = null;
    },

    // Reset actions
    resetAds: (state) => {
      state.ads = [];
      state.selectedAd = null;
      state.pagination = {
        page: 0,
        pageSize: 10,
        total: 0,
        totalPages: 0,
      };
      state.currentPage = 1;
      state.loading = false;
      state.loadingSingle = false;
      state.exporting = false;
      state.uploading = false;
      state.updating = false;
      state.deleting = false;
      state.selectedAdType = "";
      state.searchQuery = "";
      state.statusFilter = "";
      state.showUploadModal = false;
      state.showEditModal = false;
      state.showDeleteModal = false;
      state.error = null;
      state.uploadError = null;
      state.updateError = null;
      state.deleteError = null;
    },
  },
});

export const {
  // Data actions
  setAds,
  addAd,
  updateAd,
  removeAd,
  setSelectedAd,
  setAdAnalytics,
  setSingleAdAnalytics,

  // Pagination actions
  setPagination,
  setCurrentPage,

  // Loading actions
  setLoading,
  setLoadingSingle,
  setExporting,
  setUploading,
  setUpdating,
  setDeleting,

  // Filter actions
  setSelectedAdType,
  setSearchQuery,
  setStatusFilter,

  // UI actions
  setShowUploadModal,
  setShowEditModal,
  setShowDeleteModal,

  // Error actions
  setError,
  setUploadError,
  setUpdateError,
  setDeleteError,
  clearErrors,

  // Reset actions
  resetAds,
} = adsSlice.actions;

export default adsSlice.reducer;
